import { ref, computed, readonly, watch } from "vue";
import MatchService from "@/services/match.service";
import StorageService from "@/services/storage.service";
import Toast from "@/componentables/toast";
import i18n from "@/i18n";
import { useConnectivity } from "@/composables/useConnectivity";

/**
 * Offline-safe queue for prediction saves.
 *
 * Predictions are saved optimistically (the value is already on screen). When a
 * save fails for a *retryable* reason (no network / 5xx) we stash the latest
 * snapshot here so it is never silently lost, and replay it once connectivity
 * and the API recover. Permanent rejections (417 match-started, 422 validation)
 * are dropped — retrying them can't help.
 *
 * One entry per matchId (last-write-wins) collapses keystroke bursts and makes
 * out-of-order saves harmless: only the newest snapshot is ever replayed.
 *
 * Singleton: state lives at module scope so MatchItem instances, the interceptor
 * and the banner all observe the same queue.
 */

const STORAGE_KEY = "pending_predictions";

export interface PredictionPayload {
    home_team_predicted_score: number | null;
    away_team_predicted_score: number | null;
    // Edit timestamp (ms epoch). Sent on replay so the server can drop this
    // entry if a newer prediction has landed in the meantime.
    client_updated_at: number;
}

export interface PendingPrediction {
    matchId: string;
    groupId: string;
    payload: PredictionPayload;
    updatedAt: number;
    attempts: number;
}

type QueueMap = Record<string, PendingPrediction>;

const entries = ref<QueueMap>({});
let initialized = false;
let flushing = false;

function persist() {
    // @ionic/storage serialises objects; we stringify to satisfy the typed API.
    void StorageService.set(STORAGE_KEY, JSON.stringify(entries.value));
}

function nowMs(): number {
    return Date.now();
}

function enqueue(
    matchId: string,
    groupId: string,
    payload: PredictionPayload,
): void {
    const previous = entries.value[matchId];
    entries.value = {
        ...entries.value,
        [matchId]: {
            matchId,
            groupId,
            payload,
            updatedAt: nowMs(),
            attempts: previous?.attempts ?? 0,
        },
    };
    persist();
}

function remove(matchId: string): void {
    if (!entries.value[matchId]) return;
    const next = { ...entries.value };
    delete next[matchId];
    entries.value = next;
    persist();
}

/** Successful save elsewhere — drop any stale queued entry for this match. */
function resolve(matchId: string): void {
    remove(matchId);
}

/** Permanent rejection — never retry this entry again. */
function drop(matchId: string): void {
    remove(matchId);
}

function queueStatus(matchId: string): "none" | "queued" {
    return entries.value[matchId] ? "queued" : "none";
}

const pendingCount = computed(() => Object.keys(entries.value).length);

async function flush(): Promise<void> {
    if (flushing) return;
    const status = useConnectivity().connectivityStatus.value;
    if (status !== "ONLINE") return;

    flushing = true;
    try {
        // Oldest first so corrections submitted earlier don't clobber later ones.
        const ordered = Object.values(entries.value).sort(
            (a, b) => a.updatedAt - b.updatedAt,
        );

        for (const entry of ordered) {
            // Entry may have been resolved/overwritten while we awaited a prior PUT.
            const current = entries.value[entry.matchId];
            if (!current) continue;

            try {
                await MatchService.update(
                    current.groupId,
                    current.matchId,
                    current.payload,
                );
                resolve(current.matchId);
            } catch (error: any) {
                const code = error?.response?.status;
                if (code === 417) {
                    drop(current.matchId);
                    new Toast().open(
                        i18n.global.t("group.show.match-already-started"),
                    );
                } else if (code === 422) {
                    // Validation: interceptor surfaces the message; don't retry.
                    drop(current.matchId);
                } else {
                    // Retryable (network / 5xx): keep it, stop — we're degraded.
                    current.attempts += 1;
                    persist();
                    break;
                }
            }
        }
    } finally {
        flushing = false;
    }
}

async function init(): Promise<void> {
    if (initialized) return;
    initialized = true;

    try {
        const raw = await StorageService.get(STORAGE_KEY);
        if (raw && typeof raw === "string") {
            entries.value = JSON.parse(raw) as QueueMap;
        } else if (raw && typeof raw === "object") {
            entries.value = raw as QueueMap;
        }
    } catch {
        entries.value = {};
    }

    // Replay automatically the moment connectivity + API are healthy again.
    watch(
        () => useConnectivity().connectivityStatus.value,
        (status) => {
            if (status === "ONLINE") void flush();
        },
    );

    void flush();
}

export function usePredictionQueue() {
    return {
        entries: readonly(entries),
        pendingCount,
        init,
        enqueue,
        resolve,
        drop,
        queueStatus,
        flush,
    };
}
